import { captureError, addBreadcrumb } from './sentry';
import {
  getErrorMessage,
  shouldIgnoreSentryException,
} from './sentryFilters';

export {
  getErrorMessage,
  isExpectedUserError,
  isNetworkError,
  isTransientSupabaseError,
  shouldIgnoreSentryException,
} from './sentryFilters';

export interface ErrorContext {
  hook?: string;
  component?: string;
  action?: string;
  metadata?: Record<string, any>;
}

// Supabase error object structure
interface SupabaseError {
  code?: string;
  details?: string;
  hint?: string;
  message?: string;
}

// Stripe error object structure
interface StripeError {
  type?: string;
  code?: string;
  decline_code?: string;
  message?: string;
}

/**
 * Determines if an error is an app/system error (should go to Sentry)
 * vs an expected payment failure (handled by Stripe dashboard).
 * 
 * Card declines, insufficient funds, expired cards etc. are NOT app errors -
 * they're expected payment outcomes that Stripe tracks.
 */
export function isAppError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return true;
  
  const stripeError = error as StripeError;
  
  // Stripe card errors are expected payment failures - NOT app errors
  if (stripeError.type === 'card_error') return false;
  if (stripeError.code?.startsWith('card_')) return false;
  if (stripeError.decline_code) return false;
  
  // Stripe validation errors (invalid params etc.) - also not app errors
  if (stripeError.type === 'validation_error') return false;
  
  // Check for authentication/payment failure messages - these are user-side issues, not app bugs
  const message = getErrorMessage(error).toLowerCase();
  const expectedPaymentFailurePatterns = [
    'unable to authenticate your payment method',
    'authentication failed',
    'authentication_required',
    'payment_intent_authentication_failure',
    'requires_action',
    'your card was declined',
    'card was declined',
    'insufficient funds',
    'expired card',
    'incorrect cvc',
    'incorrect_cvc',
    'processing_error',
    'try again',
    'choose a different payment method',
    '3d secure',
    'secure authentication',
    'cancelled',
    'canceled',
  ];
  
  if (expectedPaymentFailurePatterns.some(pattern => message.includes(pattern))) {
    return false;
  }
  
  // Everything else is potentially an app error
  return true;
}

/**
 * Determines if an error is a CRITICAL payment error - meaning the user
 * was charged but didn't get access. These need immediate attention.
 */
export function isCriticalPaymentError(error: unknown): boolean {
  const message = getErrorMessage(error).toLowerCase();
  
  return (
    message.includes('failed to grant') ||
    message.includes('access') ||
    message.includes('confirm-payment') ||
    message.includes('backend confirmation')
  );
}


/**
 * Converts technical error messages into user-friendly messages.
 * Stripe decline messages are passed through as-is since they're already user-friendly.
 */
export function getUserFriendlyPaymentMessage(error: unknown): string {
  const stripeError = error as StripeError;
  
  // Stripe card errors already have user-friendly messages
  if (stripeError?.type === 'card_error' || stripeError?.decline_code) {
    return stripeError.message || 'Your card was declined. Please try a different payment method.';
  }
  
  const message = getErrorMessage(error).toLowerCase();
  
  // Critical: Payment succeeded but access not granted
  if (message.includes('failed to grant') || message.includes('grant competition access')) {
    return 'Your payment was successful, but we had trouble activating your entry. Please contact support and we will resolve this immediately.';
  }
  
  // Payment verification issues
  if (message.includes('payment verification') || message.includes('verify payment')) {
    return 'We could not verify your payment. If you were charged, please contact support with your payment details.';
  }
  
  // Network/connection issues
  if (message.includes('network') || message.includes('fetch') || message.includes('connection')) {
    return 'Connection issue. Please check your internet and try again.';
  }
  
  // Session/auth issues
  if (message.includes('session') || message.includes('logged in') || message.includes('expired')) {
    return 'Your session has expired. Please refresh the page and try again.';
  }
  
  // Backend confirmation failures
  if (message.includes('backend') || message.includes('confirm payment')) {
    return 'We had trouble confirming your payment. If you were charged, please contact support.';
  }
  
  // Generic fallback
  return 'Something went wrong. Please try again or contact support if the issue persists.';
}

// Convert any error-like object to a proper Error instance
function normalizeError(error: unknown): Error {
  // Already an Error instance
  if (error instanceof Error) {
    return error;
  }

  // Supabase error object (has code, details, hint, message)
  if (error && typeof error === 'object') {
    const supabaseError = error as SupabaseError;
    if ('message' in supabaseError || 'code' in supabaseError) {
      const errorMessage = supabaseError.message || supabaseError.code || 'Unknown Supabase error';
      const normalizedError = new Error(errorMessage);
      normalizedError.name = 'SupabaseError';
      
      // Attach additional context as properties
      (normalizedError as any).code = supabaseError.code;
      (normalizedError as any).details = supabaseError.details;
      (normalizedError as any).hint = supabaseError.hint;
      
      return normalizedError;
    }
  }

  // String error
  if (typeof error === 'string') {
    return new Error(error);
  }

  // Fallback for unknown error types
  return new Error(String(error));
}

export function handleError(error: unknown, context: ErrorContext): void {
  const { hook, component, action, metadata } = context;
  
  // Normalize the error to a proper Error instance
  const normalizedError = normalizeError(error);

  if (import.meta.env.DEV) {
    console.error(`[Error in ${hook || component}]`, {
      error: normalizedError,
      originalError: error,
      action,
      metadata,
    });
  }

  if (shouldIgnoreSentryException(error) || shouldIgnoreSentryException(normalizedError)) {
    if (import.meta.env.DEV) {
      console.log('[Expected/user/network error - not reported to Sentry]', normalizedError.message);
    }
    return;
  }

  const contextData: Record<string, any> = {};

  if (hook) contextData.hook = hook;
  if (component) contextData.component = component;
  if (action) contextData.action = action;
  if (metadata) contextData.metadata = metadata;
  
  // Include original error properties in context for Supabase errors
  if (error && typeof error === 'object' && !(error instanceof Error)) {
    const supabaseError = error as SupabaseError;
    if (supabaseError.code) contextData.supabaseCode = supabaseError.code;
    if (supabaseError.details) contextData.supabaseDetails = supabaseError.details;
    if (supabaseError.hint) contextData.supabaseHint = supabaseError.hint;
  }

  captureError(normalizedError, contextData, 'error');
}

export function wrapAsyncFunction<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  context: ErrorContext
): T {
  return (async (...args: Parameters<T>) => {
    try {
      addBreadcrumb(
        `Starting ${context.action || 'operation'}`,
        context.hook || context.component || 'async',
        'info',
        context.metadata
      );

      const result = await fn(...args);

      addBreadcrumb(
        `Completed ${context.action || 'operation'}`,
        context.hook || context.component || 'async',
        'info'
      );

      return result;
    } catch (error) {
      handleError(error, context);
      throw error;
    }
  }) as T;
}

export function withErrorHandling<T extends (...args: any[]) => any>(
  fn: T,
  context: ErrorContext
): T {
  return ((...args: Parameters<T>) => {
    try {
      return fn(...args);
    } catch (error) {
      handleError(error, context);
      throw error;
    }
  }) as T;
}
